import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Box, Heading, Modal } from '@zillow/constellation-10';

import { RMWContext } from '../../../providers/RMWContext';

import { PropertiesSelectors } from '../../../selectors';

import PostingActions from '../../../actions/PostingActions';

import ModalFooter from '../ModalFooter';
import SpinnerContainer from '../../SpinnerContainer';

import PropertyToConvert from './PropertyToConvert';
import UnitNumber from './UnitNumber';
import Congratulations from './Congratulations';

import { STEPPER, STEPS } from './constants';

import { ConvertableItem, ConvertableOption } from './Interface';

const propTypes = {
  setShowMultiToSingleFamilyConversionModal: PropTypes.func.isRequired,
};
const defaultProps = {};

const MultiToSingleFamilyConversionModal = ({ setShowMultiToSingleFamilyConversionModal }) => {
  const dispatch = useDispatch();

  const { paths } = useContext(RMWContext);

  const currentProperty = useSelector((state) => PropertiesSelectors.makeCurrentProperty(state));
  const { isAddressValid, floorplans } = currentProperty;

  const [convertableList, setConvertableList] = useState([]);
  const [selectedItem, setSelectedItem] = useState(ConvertableItem({}));

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState('');
  const [convertedListingAlias, setConvertedListingAlias] = useState('');

  const [step, setStep] = useState(STEPS.INTERMITTENT);

  const hideCancelButton = convertableList.length === 1 || step === STEPS.INTERMITTENT || step === STEPS.CONGRATULATE;
  const isSubmitDisabled = !selectedItem.id || step === STEPS.INTERMITTENT;

  useEffect(() => {
    const tempConvertableList = [];
    floorplans.forEach((floorplan) => {
      if (floorplan.units.length) {
        floorplan.units.forEach((unit) => {
          const { baths, beds, squareFeet, name } = floorplan;
          const finalSquareFeet = unit.squareFeet || squareFeet;
          const unitToConvert = { ...unit, baths, beds, squareFeet: finalSquareFeet, floorplanName: name };

          tempConvertableList.push(ConvertableOption(unitToConvert));
        });
      } else {
        if (tempConvertableList.length && !floorplan.name) return;

        tempConvertableList.push(ConvertableOption(floorplan));
      }
    });

    setConvertableList(tempConvertableList);

    // if there is only one choice inside the conversion list,
    // then proceed to second step
    if (tempConvertableList.length === 1) {
      setSelectedItem(ConvertableItem(tempConvertableList[0].convertableItem));
      setStep(STEPS.UNIT_NUMBER);
    } else {
      setStep(STEPS.PROPERTY);
    }
  }, []);

  const handleOptionSelect = (newOption) => {
    setSelectedItem(ConvertableItem(newOption.convertableItem));
    setSubmitErrorMessage('');
  };

  const handleModalClose = (isModalDismissed = false, closeButtonPressed = false) => {
    if (step === STEPS.UNIT_NUMBER && !closeButtonPressed) {
      setStep(STEPS.PROPERTY);
      return;
    }

    setShowMultiToSingleFamilyConversionModal(false);
  };

  const handleSubmit = () => {
    if (step === STEPS.PROPERTY) {
      setStep(STEPS.UNIT_NUMBER);
      return;
    }
    if (step === STEPS.CONGRATULATE) {
      const pathToRoute = `${paths.propertiesPage}/${convertedListingAlias}/listing`;

      window.router.transitionTo(pathToRoute);
      return;
    }

    const { id: modelId, unitName: unit } = selectedItem;
    setIsSubmitting(true);

    dispatch(PostingActions.property.convertFloorplanOrUnitToNucleus(modelId, unit))
      .then((response) => {
        const { listingAlias } = response;

        setConvertedListingAlias(listingAlias);

        setStep(STEPS.CONGRATULATE);
      })
      .catch((err) => {
        const { message } = err;

        setSubmitErrorMessage(message);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleUnitNumberBlur = (newUnitNumber) => {
    if (newUnitNumber !== selectedItem.unitName) {
      setSelectedItem({
        ...selectedItem,
        unitName: newUnitNumber,
      });
      setSubmitErrorMessage('');
    }
  };

  const handleModalReset = () => {
    setSelectedItem(ConvertableItem({}));
    setStep(STEPS.PROPERTY);
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && handleModalClose(true, true)}
      header={<Heading level={2}>Convert floor plan/unit to a new listing</Heading>}
      body={
        <div data-testid="MultiToSingleFamilyConversionModal">
          {isAddressValid ? (
            <Box css={{ flexGrow: 0, flexShrink: 0, flexBasis: '100%' }}>
              {step === STEPS.PROPERTY && (
                <PropertyToConvert
                  convertableList={convertableList}
                  handleOptionSelect={handleOptionSelect}
                  selectedItem={selectedItem}
                />
              )}
              {step === STEPS.UNIT_NUMBER && (
                <UnitNumber
                  handleUnitNumberBlur={handleUnitNumberBlur}
                  selectedItem={selectedItem}
                  submitErrorMessage={submitErrorMessage}
                />
              )}
              {step === STEPS.CONGRATULATE && (
                <Congratulations convertedListingAlias={convertedListingAlias} handleModalReset={handleModalReset} />
              )}
              {step === STEPS.INTERMITTENT && <SpinnerContainer />}
            </Box>
          ) : (
            <Box css={{ flexGrow: 0, flexShrink: 0, flexBasis: '100%' }}>
              <Heading level="3" fontFamily="serif">
                Cannot convert since the address is invalid.
              </Heading>
            </Box>
          )}
        </div>
      }
      footer={
        <ModalFooter
          onCancel={hideCancelButton ? null : () => handleModalClose(true)}
          onSubmit={handleSubmit}
          submitText={STEPPER[step].submitText}
          cancelText={STEPPER[step].cancelText}
          isSubmitting={isSubmitting}
          isSubmitDisabled={isSubmitDisabled}
        />
      }
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

MultiToSingleFamilyConversionModal.propTypes = propTypes;
MultiToSingleFamilyConversionModal.defaultProps = defaultProps;

export default MultiToSingleFamilyConversionModal;
